// frontend/src/core/theme/ThemeProvider.tsx
//
// Worqly theme provider.
//
//   mode      'light' | 'dark' | 'system'   ← what the user picked
//   resolved  'light' | 'dark'              ← the effective theme right now
//
// Bugs we fix in this rebuild
//   1. FOUC on first paint — `applyHtmlClass` used to only run inside a
//      `useEffect`, so the very first React render mounted with the html
//      element in light mode even when the user had picked dark. The class
//      flip was visible to the user as a flash. We now apply the resolved
//      theme synchronously at module load (and again via useLayoutEffect,
//      which beats useEffect to paint).
//   2. `readInitialMode()` was being called twice during the initial
//      `useState` setup. Trimmed to a single call.
//   3. Listener for `prefers-color-scheme: dark` was only installed while
//      `mode === 'system'`, but it was reattached on every mode change.
//      Moved its lifecycle off the `mode` effect so it doesn't leak.
//   4. `setMode` would throw if `window` was unavailable. Guarded.

import * as React from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'worqly_theme';

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (m: ThemeMode) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function readInitialMode(): ThemeMode {
  // Default is 'light'. We only open in dark mode when the user has
  // previously and explicitly chosen it (persisted in localStorage). A fresh
  // visitor — or someone whose OS happens to be in dark mode — gets light.
  if (typeof window === 'undefined') return 'light';
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'light';
}

function resolve(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  }
  return mode;
}

function applyHtmlClass(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Inform the browser so form controls + native UI render with the right tone.
  root.style.colorScheme = theme;
}

// Run once at module load so the dark class is on <html> before React's
// first paint. Prevents the light→dark flash that older versions of this
// provider had.
if (typeof window !== 'undefined') {
  applyHtmlClass(resolve(readInitialMode()));
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Read storage exactly once.
  const initialMode = React.useMemo(readInitialMode, []);
  const [mode, setModeState] = React.useState<ThemeMode>(initialMode);
  const [resolved, setResolved] = React.useState<'light' | 'dark'>(() => resolve(initialMode));

  // Apply the resolved theme synchronously before paint each time `mode`
  // changes. useLayoutEffect (vs useEffect) avoids any visible flash.
  React.useLayoutEffect(() => {
    const next = resolve(mode);
    setResolved(next);
    applyHtmlClass(next);
  }, [mode]);

  // Watch the OS preference once. The handler reads `mode` via a ref so the
  // listener does not need to reattach on every toggle (which previously
  // captured stale closures and could miss / double-fire during rapid switches).
  const modeRef = React.useRef(mode);
  React.useEffect(() => { modeRef.current = mode; }, [mode]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (modeRef.current !== 'system') return;
      const r: 'light' | 'dark' = mq.matches ? 'dark' : 'light';
      setResolved(r);
      applyHtmlClass(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setMode = React.useCallback((m: ThemeMode) => {
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore quota / privacy mode */ }
    }
    setModeState(m);
  }, []);

  const value = React.useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const v = React.useContext(ThemeContext);
  if (!v) throw new Error('useTheme must be inside <ThemeProvider>');
  return v;
}
